import "@/App.css";
import "@/i18n";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import PublicForm from "@/pages/PublicForm";
import FormSuccess from "@/pages/FormSuccess";
import DepartmentPage from "@/pages/DepartmentPage";
import AdminLogin from "@/pages/AdminLogin";
import AdminLayout from "@/layouts/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import Forms from "@/pages/admin/Forms";
import FormEditor from "@/pages/admin/FormEditor";
import Approvals from "@/pages/admin/Approvals";
import Submissions from "@/pages/admin/Submissions";
import SubmissionDetail from "@/pages/admin/SubmissionDetail";
import Departments from "@/pages/admin/Departments";
import Users from "@/pages/admin/Users";
import GlobalLists from "@/pages/admin/GlobalLists";
import Settings from "@/pages/admin/Settings";
import Templates from "@/pages/admin/Templates";
import ChangePassword from "@/pages/admin/ChangePassword";
import BrandingSettings from "@/pages/admin/BrandingSettings";
import DepartmentDashboard from "@/pages/admin/DepartmentDashboard";
import SecurityDashboard from "@/pages/admin/SecurityDashboard";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Navigate to="/admin/dashboard" replace />} />
      <Route path="/form/:slug" element={<PublicForm />} />
      <Route path="/form/:slug/success" element={<FormSuccess />} />
      
      {/* Department Landing Page */}
      <Route path="/:departmentSlug" element={<DepartmentPage />} />
      
      {/* Department Form by Slug */}
      <Route path="/:departmentSlug/:formSlug" element={<PublicForm />} />
      <Route path="/:departmentSlug/:formSlug/success" element={<FormSuccess />} />
      
      {/* Shortlink */}
      <Route path="/f/:shortcode" element={<PublicForm />} />
      <Route path="/f/:shortcode/success" element={<FormSuccess />} />
      
      {/* Admin Routes */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin/change-password" element={
        <ProtectedRoute>
          <ChangePassword />
        </ProtectedRoute>
      } />
      
      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="forms" element={<Forms />} />
        <Route path="forms/new" element={<FormEditor />} />
        <Route path="forms/:id/edit" element={<FormEditor />} />
        <Route path="approvals" element={<Approvals />} />
        <Route path="submissions" element={<Submissions />} />
        <Route path="submissions/:id" element={<SubmissionDetail />} />
        <Route path="departments" element={<Departments />} />
        <Route path="departments/:id/dashboard" element={<DepartmentDashboard />} />
        <Route path="users" element={<Users />} />
        <Route path="global-lists" element={<GlobalLists />} />
        <Route path="templates" element={<Templates />} />
        <Route path="settings" element={<Settings />} />
        <Route path="branding" element={<BrandingSettings />} />
        <Route path="security" element={<SecurityDashboard />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
